import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { Star, X } from 'lucide-react';
import type { SortablePhoto } from '../types/apartment';
import { PhotoImg } from './PhotoImg';

interface SortablePhotoThumbnailProps {
  photo: SortablePhoto;
  isPrimary: boolean;
  onMarkAsPrimary: () => void;
  onDelete: () => void;
  /** True when rendered inside DragOverlay — disables useSortable transforms to avoid double-applying. */
  isOverlay?: boolean;
}

/**
 * Individual draggable thumbnail card inside the photo carousel.
 * Uses @dnd-kit/sortable for drag-and-drop positioning.
 *
 * Tailwind `touch-none` maps to touch-action: none — required by @dnd-kit PointerSensor.
 * Inline `transform` style must NOT be applied via Tailwind classes as @dnd-kit injects
 * it directly on the element style; mixing the two causes animation conflicts.
 */
export function SortablePhotoThumbnail({
  photo,
  isPrimary,
  onMarkAsPrimary,
  onDelete,
  isOverlay = false,
}: SortablePhotoThumbnailProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: photo.key,
    // When rendering inside DragOverlay we still call useSortable (required for the hook
    // to register), but we don't apply the transform — DragOverlay positions itself.
    disabled: isOverlay,
  });

  const style: React.CSSProperties = isOverlay
    ? {}
    : {
        transform: CSS.Transform.toString(transform),
        transition,
      };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative flex-shrink-0 w-28 h-28 rounded-lg overflow-hidden border-2 select-none touch-none will-change-transform ${
        isDragging && !isOverlay ? 'opacity-40 border-blue-400' : 'border-transparent'
      } ${isOverlay ? 'shadow-xl ring-2 ring-blue-500' : ''}`}
      {...(!isOverlay ? attributes : {})}
      {...(!isOverlay ? listeners : {})}
    >
      <PhotoImg
        src={photo.thumbnailUrl}
        fallbackSrc={photo.photoUrl}
        alt="Apartment photo"
        className="w-full h-full object-cover cursor-grab active:cursor-grabbing"
        draggable={false}
      />

      {/* Primary badge */}
      {isPrimary && (
        <div className="absolute top-1 left-1 bg-yellow-400 text-yellow-900 rounded-full p-0.5" aria-label="Primary photo">
          <Star size={12} fill="currentColor" />
        </div>
      )}

      {/* Mark as Primary button — hidden when already primary */}
      {!isPrimary && (
        <button
          type="button"
          aria-label="Mark as primary"
          className="absolute top-1 left-1 p-0.5 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            onMarkAsPrimary();
          }}
        >
          <Star size={12} />
        </button>
      )}

      {/* Delete button */}
      <button
        type="button"
        aria-label="Remove photo"
        className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        <X size={12} />
      </button>
    </div>
  );
}
